import { forwardRef, HTMLProps, Ref } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { fontStyles } from '@/styles/typography';

const badgeStyles = tv({
  slots: {
    root: 'px-1.5 py-0.5 rounded-[26px] w-fit data-[hide-badge=true]:invisible',
    content: 'before:content-none after:content-none',
  },
  variants: {
    variant: {
      neutral: {
        root: 'bg-token-gray-200',
        content: fontStyles({ variant: 'CAPTION', class: 'text-token-gray-800' }),
      },
      positive: {
        root: 'bg-token-green-300',
        content: fontStyles({ variant: 'CAPTION', class: 'text-token-green-1200' }),
      },
      negative: {
        root: 'bg-token-red-300',
        content: fontStyles({ variant: 'CAPTION', class: 'text-token-red-1000' }),
      },
      neutralOutline: {
        root: 'bg-token-gray-200 border-token-gray-500 text-token-gray-800 h-[18px] flex items-center justify-center p-0 whitespace-nowrap border border-solid cursor-default min-w-[100px] hover:bg-token-gray-300 hover:border-token-gray-600',
        content: fontStyles({ variant: 'LABEL_XS', class: 'px-2 text-token-gray-800' }),
      },
    },
  },
});

type BadgeStylesProps = VariantProps<typeof badgeStyles>;
export type BadgeVariants = NonNullable<BadgeStylesProps['variant']>;

type BadgeProps = HTMLProps<HTMLDivElement> & {
  variant: BadgeVariants;
};

export const Badge = forwardRef(function Badge(
  { variant, content, children, role = 'status', className, ...divProps }: BadgeProps,
  ref: Ref<HTMLDivElement>,
) {
  const { root, content: contentClass } = badgeStyles({ variant });

  return (
    <div ref={ref} className={root({ class: className })} role={role} {...divProps}>
      <div className={contentClass()}>{children}</div>
    </div>
  );
});
